import React, { FC, useState } from 'react'

import { httpsCallable } from 'firebase/functions'

import { PrivateLiveToggle } from '../../../../../components/elements/toggles/toggles/PrivateLiveToggle'
import { Separator } from '../../../../../components/ui/separator'

import { formatNotificationTimestamp } from '../../../../../helpers/time'
import useToasterHelper from '../../../../../helpers/ToasterHelper'

import FirebaseApp from '../../../../../services/firebaseApp'

import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'
import { PublishItem } from '../../ManageClinicNOtherOperationsTab/types/publish.types'
import { TGroupPublish } from '../../../../../models/event-scheduling/event-scheduling.interface'

type INotificationType = 'orderOfGo' | 'scores'
type IKeyType = 'publishOrder' | 'publishScore'

interface PublishNotificationsItemProps {
  item: PublishItem
  eventId?: string
  publishItems: PublishItem[]
  publishState: TGroupPublish
  setPublishState: (state: TGroupPublish | ((prev: TGroupPublish) => TGroupPublish)) => void
  isSaving: boolean
  scheduleBy: 'class' | 'rider'
  registrationsByDay: IRegistrationByDayInterface[]
  registrationsGroupedByDay: Record<string, any[]>
  lastSentTimes: Record<string, string>
  setLastSentTimes: (
    state: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => void
}

export const PublishNotificationsItem: FC<PublishNotificationsItemProps> = ({
  item,
  eventId,
  publishItems,
  publishState,
  setPublishState,
  isSaving,
  scheduleBy,
  registrationsByDay,
  registrationsGroupedByDay,
  lastSentTimes,
  setLastSentTimes,
}) => {
  const toastFunctions = useToasterHelper()

  const [sendingNotifications, setSendingNotifications] = useState<Record<string, boolean>>({})
  const orderOfGoNotificationKey = `${item.id}-orderOfGo`
  const scoresNotificationKey = `${item.id}-scores`
  const isOrderOfGoSending = sendingNotifications[orderOfGoNotificationKey]
  const isScoresSending = sendingNotifications[scoresNotificationKey]

  function updateAllClasses(
    prev: TGroupPublish,
    publishItems: { id: string }[],
    key: IKeyType
  ): TGroupPublish {
    const currentAllState = publishState['all-classes']?.[key] ?? false
    const newState = !currentAllState

    const updated = { ...prev }

    // update all individual days
    publishItems.forEach((item) => {
      if (item.id !== 'all-classes') {
        updated[item.id] = {
          ...updated[item.id],
          [key]: newState,
        }
      }
    })

    // update all-classes state
    updated['all-classes'] = {
      ...updated['all-classes'],
      [key]: newState,
    }

    return updated
  }

  const updateClasses = (id: string, prev: TGroupPublish, key: IKeyType) => {
    const updated = {
      ...prev,
      [id]: {
        ...prev[id],
        [key]: !prev[id]?.[key],
      },
    }

    // after individual toggle - check if all days now published
    const allDaysPublished = publishItems
      .filter((item) => item.id !== 'all-classes')
      .every((item) => updated[item.id]?.[key] === true)

    // update all-classes state to curr status
    updated['all-classes'] = {
      ...updated['all-classes'],
      [key]: allDaysPublished,
    }
    return updated
  }

  const onToggle = (id: string, key: IKeyType) => {
    if (id === 'all-classes') {
      const newState = updateAllClasses(publishState, publishItems, key)
      setPublishState(newState)
    } else {
      const newState = updateClasses(id, publishState, key)
      setPublishState(newState)
    }
  }

  const getRegistrationCount = (itemId: string) => {
    if (itemId === 'all-classes') {
      if (scheduleBy === 'class') {
        // class mode -count all registrations across all classes
        return registrationsByDay?.length || 0
      } else {
        // rider mode - count all registrations across all days
        return Object.values(registrationsGroupedByDay).reduce((total, dayRegistrations) => {
          const validRegistrations = dayRegistrations.filter(
            (registration) =>
              registration &&
              registration.userId &&
              !('isBreakDrag' in registration && registration.isBreakDrag)
          )
          return total + validRegistrations.length
        }, 0)
      }
    }

    if (scheduleBy === 'class') {
      return (
        registrationsByDay?.filter((registration) => registration && registration.uuid === itemId)
          .length || 0
      )
    } else {
      // rider mode
      const registrationsForDay = registrationsGroupedByDay?.[itemId] || []
      return (
        registrationsForDay.filter(
          (registration) =>
            registration &&
            registration.userId &&
            !('isBreakDrag' in registration && registration.isBreakDrag)
        ).length || 0
      )
    }
  }

  const onSendPublishNotification = async (
    notificationType: INotificationType,
    scheduleType: 'classGroupPublish' | 'riderGroupPublish',
    items: { groupKey: string; riders: IRegistrationByDayInterface[] }[]
  ) => {
    const data = { eventId, notificationType, scheduleType, items }

    const sendPublishNotification = httpsCallable(FirebaseApp.functions, 'onSendPublishNotification')
    void sendPublishNotification(data)

    const now = new Date()
    const lastSentDisplay = formatNotificationTimestamp(now)
    
    items.forEach((item) => {
      setLastSentTimes((prev) => ({
        ...prev,
        [`${item.groupKey}-${notificationType}`]: lastSentDisplay,
      }))
    })
  }

  const sendClassNotification = async (classId: string, notificationType: INotificationType) => {
    const notificationKey = `${classId}-${notificationType}`
    if (sendingNotifications[notificationKey]) return
    setSendingNotifications((prev) => ({ ...prev, [notificationKey]: true }))

    let count = 0
    try {
      if (classId === 'all-classes') {
        const items = []
        const allClassIds = publishItems.map((item) => item.id)
        for (const individualClassId of allClassIds) {
          const actualRegistrations =
            registrationsByDay?.filter((registration) => registration.uuid === individualClassId) ||
            []
          items.push({
            groupKey: individualClassId,
            riders: actualRegistrations,
          })
          count += actualRegistrations.length
        }

        await onSendPublishNotification(notificationType, 'classGroupPublish', items)
      } else {
        const actualRegistrations =
          registrationsByDay?.filter((registration) => registration.uuid === classId) || []
        const items = [{ groupKey: classId, riders: actualRegistrations }]

        await onSendPublishNotification(notificationType, 'classGroupPublish', items)
        count = actualRegistrations.length
      }

      setTimeout(() => {
        toastFunctions.success({
          message: `${count} ${notificationType === 'orderOfGo' ? 'ride time' : 'results'} notification(s) sent successfully`,
        })
      }, 1000)
    } catch (error) {
      console.error('Error sending class notification:', error)
      toastFunctions.error({
        message: `Failed to send ${notificationType === 'orderOfGo' ? 'ride time' : 'results'} notifications`,
      })
    } finally {
      setTimeout(() => {
        setSendingNotifications((prev) => ({ ...prev, [notificationKey]: false }))
      }, 1000)
    }
  }

  const sendRiderNotification = async (dayId: string, notificationType: INotificationType) => {
    const notificationKey = `${dayId}-${notificationType}`
    let count = 0

    if (sendingNotifications[notificationKey]) return
    setSendingNotifications((prev) => ({ ...prev, [notificationKey]: true }))

    try {
      if (dayId === 'all-classes') {
        const items = []
        const allDayIds = publishItems.map((item) => item.id)
        for (const individualDayId of allDayIds) {
          const registrationsForDay = registrationsGroupedByDay?.[individualDayId] || []
          const actualRegistrations: IRegistrationByDayInterface[] = registrationsForDay.filter(
            (registration) => !('isBreakDrag' in registration && registration.isBreakDrag)
          )
          items.push({
            groupKey: individualDayId,
            riders: actualRegistrations,
          })
          count += actualRegistrations.length
        }
        await onSendPublishNotification(notificationType, 'riderGroupPublish', items)
      } else {
        const registrationsForDay = registrationsGroupedByDay?.[dayId] || []
        const actualRegistrations: IRegistrationByDayInterface[] = registrationsForDay.filter(
          (registration) =>
            registration.userId && !('isBreakDrag' in registration && registration.isBreakDrag)
        )
        const items = [{ groupKey: dayId, riders: actualRegistrations }]

        await onSendPublishNotification(notificationType, 'riderGroupPublish', items)
        count = actualRegistrations.length
      }

      toastFunctions.success({
        message: `${count} ${notificationType === 'orderOfGo' ? 'ride time' : 'results'} notification(s) sent successfully`,
      })
    } catch (error) {
      console.error('Error sending notification:', error)
      toastFunctions.error({
        message: `Failed to send ${notificationType === 'orderOfGo' ? 'ride time' : 'results'} notifications`,
      })
    } finally {
      setSendingNotifications((prev) => ({ ...prev, [notificationKey]: false }))
    }
  }

  const sendNotification = async (itemId: string, notificationType: INotificationType) => {
    if (scheduleBy === 'class') {
      return sendClassNotification(itemId, notificationType)
    } else {
      return sendRiderNotification(itemId, notificationType)
    }
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-0 sm:items-start justify-between py-2 ">
        <div className="space-y-1 text-center sm:text-left">
          <h3 className="text-SeabiscuitDark200ThemeColor">{item.title}</h3>
          <p className="text-SeabiscuitDark200ThemeColor/50">{item.subtitle}</p>
        </div>

        <div className="flex flex-col space-y-3">
          <div className="flex items-center">
            <p className="text-sm text-SeabiscuitDark200ThemeColor/80 mr-3">Order of Go</p>
            <div className="flex items-center gap-3">
              <PrivateLiveToggle
                isOn={publishState[item.id]?.publishOrder}
                onToggle={() => onToggle(item.id, 'publishOrder')}
                disabled={isSaving}
              />
              <button
                className="bg-SeabiscuitDark200ThemeColor/10 transition-colors rounded-full p-2 hover:bg-SeabiscuitGreenThemeColor flex-shrink-0 flex items-center justify-center group"
                disabled={isSaving || isOrderOfGoSending}
                onClick={() => sendNotification(item.id, 'orderOfGo')}
              >
                {isOrderOfGoSending ? (
                  <div className="h-3 w-3 border-2 border-SeabiscuitMainThemeColor border-t-transparent rounded-full animate-spin" />
                ) : (
                  <img
                    src={'assets/cp_icons/bell-theme-icon.svg'}
                    alt="bell icon"
                    className="h-3 w-3 group-hover:brightness-0 group-hover:invert transition-all"
                  />
                )}
              </button>
              <div className="text-SeabiscuitDark200ThemeColor/70 w-[140px] min-w-[140px]">
                <p className="text-wrap text-[10px]">
                  {getRegistrationCount(item.id) === 0
                    ? 'No riders to notify'
                    : lastSentTimes[`${item.id}-orderOfGo`] ||
                      item.orderOfGoLastSent ||
                      'Not yet sent'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <p className="text-sm text-SeabiscuitDark200ThemeColor/80 mr-3">Scores</p>
            <div className="flex items-center gap-3">
              <PrivateLiveToggle
                isOn={publishState[item.id]?.publishScore}
                onToggle={() => onToggle(item.id, 'publishScore')}
                disabled={isSaving}
              />
              <button
                className="bg-SeabiscuitDark200ThemeColor/10 transition-colors rounded-full p-2 hover:bg-SeabiscuitGreenThemeColor flex-shrink-0 flex items-center justify-center group"
                disabled={isSaving || isScoresSending}
                onClick={() => sendNotification(item.id, 'scores')}
              >
                {isScoresSending ? (
                  <div className="h-3 w-3 border-2 border-SeabiscuitMainThemeColor border-t-transparent rounded-full animate-spin" />
                ) : (
                  <img
                    src={'assets/cp_icons/bell-theme-icon.svg'}
                    alt="bell icon"
                    className="h-3 w-3 group-hover:brightness-0 group-hover:invert transition-all"
                  />
                )}
              </button>
              <div className="text-SeabiscuitDark200ThemeColor/70 w-[140px] min-w-[140px]">
                <p className="text-wrap text-[10px]">
                  {getRegistrationCount(item.id) === 0
                    ? 'No riders to notify'
                    : lastSentTimes[`${item.id}-scores`] || item.scoresLastSent || 'Not yet sent'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Separator className="bg-SeabiscuitGray500ThemeColor/50 mt-3" />
    </div>
  )
}
